"use client"

import { useEffect } from "react"

import {
    Banknote,
    Clock,
    Database,
    GitBranch,
    Layers,
    ListOrdered,
    ScaleIcon,
    ShieldCheck,
    TrendingUp,
    type LucideIcon,
} from "lucide-react"

import { EVIDENCE_LADDER, resolvePolicy } from "@/modules/lernpfad/engine"
import type { EvidenceInstrument, Modality, RequirementOrigin, SkillSource } from "@/modules/lernpfad/engine"
import type { LernpfadDemoState } from "@/app/demo/lernpfad/use-lernpfad-demo"
import { ALL_SOURCES } from "@/app/demo/lernpfad/use-lernpfad-demo"
import { REASON_VAR } from "../charts/palette"
import { ChoiceRow, Cluster, SliderRow, SwitchRow, WeightBar } from "./param-controls"
import { CLUSTER_ORDER, overridesByCluster } from "./cluster-map"
import type { ClusterId } from "./cluster-map"
import SectionRail from "./section-rail"
import { useScrollSpy } from "./use-scroll-spy"
import { FRAMEWORK_BY_ID } from "@/modules/lernpfad/data/frameworks"

/**
 * The composer's parameters, clustered.
 *
 * Every control on this page writes a field that a line of `compose.ts` or
 * `gap.ts` actually reads — the origin weights ARE `originWeight`, the evidence
 * ladder IS `evidence.floor`. Nothing here is illustrative, which is why the
 * impact panel beside it can recompute honestly.
 *
 * Clusters that another setting has made meaningless are dimmed and labelled
 * rather than hidden: a rate card that vanishes when the ranking is in hours
 * teaches nobody why it vanished.
 */

const ORIGINS: RequirementOrigin[] = ["regulatorik", "rolle", "mag", "ziel"]
const MODALITIES: Modality[] = ["on-the-job", "social", "formal"]
const ORIGIN_COLOUR: Record<RequirementOrigin, string> = {
    regulatorik: REASON_VAR.pflicht,
    rolle: REASON_VAR.luecke,
    mag: REASON_VAR.voraussetzung,
    ziel: REASON_VAR.ziel,
}

const CLUSTER_ICON: Record<ClusterId, LucideIcon> = {
    budget: Clock,
    priority: ScaleIcon,
    evidence: ShieldCheck,
    duties: ShieldCheck,
    gaps: TrendingUp,
    sources: Database,
    sequencing: ListOrdered,
    mix: Layers,
    cost: Banknote,
    governance: GitBranch,
}

export default function ParameterClusters({
    state,
    jumpTarget,
    onJumped,
}: {
    state: LernpfadDemoState
    /** A cluster to scroll to once mounted — set by a framework's cluster pill. */
    jumpTarget?: string | null
    onJumped?: () => void
}) {
    const { t, localizer, policy, setParam, frameworks } = state
    const p = resolvePolicy(policy)
    const usdMode = p.ranking.denominator === "usd"

    const changed = overridesByCluster(policy)
    const { active, register, jumpTo } = useScrollSpy(CLUSTER_ORDER)

    // The pill that sent us here fires before this tab's sections exist, so the
    // scroll waits for the mount rather than being issued at click time.
    useEffect(() => {
        if (!jumpTarget) return
        jumpTo(jumpTarget)
        onJumped?.()
    }, [jumpTarget, jumpTo, onJumped])

    /**
     * Which framework wrote into a cluster.
     *
     * Computed by asking what each SELECTED framework alone would change, so the
     * chip names a cause rather than merely reporting that something differs.
     * With two frameworks touching one cluster the first in selection order
     * wins — naming both would not fit the header and the pulse is an attention
     * cue, not an audit trail.
     */
    const writtenBy: Partial<Record<ClusterId, string>> = {}
    for (const id of frameworks) {
        const framework = FRAMEWORK_BY_ID.get(id)
        if (!framework) continue
        const touched = overridesByCluster(framework.patch)
        for (const cluster of CLUSTER_ORDER) {
            if (touched[cluster] > 0 && !writtenBy[cluster]) {
                writtenBy[cluster] = localizer.framework(framework).name
            }
        }
    }

    const section = (id: ClusterId) => ({
        id,
        sectionRef: register(id),
        changed: changed[id],
        writtenBy: writtenBy[id],
    })

    return (
        <div className="grid gap-6 lg:grid-cols-[11.25rem_minmax(0,1fr)]">
            <SectionRail
                heading={t("steuer.tab.params")}
                active={active}
                onJump={jumpTo}
                items={CLUSTER_ORDER.map((id) => ({
                    id,
                    label: t(`steuer.cluster.${id}`),
                    icon: CLUSTER_ICON[id],
                    changed: changed[id],
                }))}
            />
            {/* Single column, deliberately. A two-column grid packs more in, but
                it makes "which section am I in" genuinely ambiguous — the left
                and right card of a row tie for topmost, and the rail highlight
                then flickers between them. Scroll-spy assumes a linear
                document, so the document is made linear. Each cluster is capped
                rather than stretched to the full width: a 900px slider reads as
                a layout accident. */}
            <div className="grid min-w-0 max-w-3xl gap-4">
            <Cluster icon={Clock} {...section("budget")}
                title={t("steuer.cluster.budget")}>
                <SliderRow
                    label={t("steuer.p.periodMonths")}
                    value={p.budget.periodMonths}
                    min={1}
                    max={12}
                    unit={t("steuer.p.months")}
                    onChange={(value) => setParam("budget.periodMonths", value)}
                />
                <SwitchRow
                    label={t("steuer.p.scaleByPensum")}
                    hint={t("steuer.p.scaleByPensumHint")}
                    checked={p.budget.scaleByPensum}
                    onChange={(value) => setParam("budget.scaleByPensum", value)}
                />
            </Cluster>

            <Cluster
                icon={ScaleIcon}
                {...section("priority")}
                title={t("steuer.cluster.priority")}
                hint={t("steuer.p.originWeightHint")}
            >
                <WeightBar
                    entries={ORIGINS.map((origin) => ({
                        id: origin,
                        label: localizer.origin(origin),
                        value: p.ranking.originWeight[origin],
                        colourVar: ORIGIN_COLOUR[origin],
                    }))}
                />
                {ORIGINS.map((origin) => (
                    <SliderRow
                        key={origin}
                        label={localizer.origin(origin)}
                        value={p.ranking.originWeight[origin]}
                        min={0}
                        max={200}
                        step={5}
                        onChange={(value) => setParam(`ranking.originWeight.${origin}`, value)}
                    />
                ))}
                <ChoiceRow
                    label={t("steuer.p.denominator")}
                    value={p.ranking.denominator}
                    options={[
                        { value: "hours" as const, label: t("steuer.p.denominator.hours") },
                        { value: "usd" as const, label: t("steuer.p.denominator.usd") },
                        { value: "flat" as const, label: t("steuer.p.denominator.flat") },
                    ]}
                    onChange={(value) => setParam("ranking.denominator", value)}
                />
            </Cluster>

            <Cluster icon={ShieldCheck} {...section("evidence")}
                title={t("steuer.cluster.evidence")}>
                <ChoiceRow<EvidenceInstrument>
                    label={t("steuer.p.floor")}
                    hint={t("steuer.p.floorHint")}
                    value={p.evidence.floor}
                    options={EVIDENCE_LADDER.map((instrument) => ({
                        value: instrument,
                        label: t(`instrument.${instrument}`),
                    }))}
                    onChange={(value) => setParam("evidence.floor", value)}
                />
                <SwitchRow
                    label={t("steuer.p.gateSkillCredit")}
                    checked={p.evidence.gateSkillCredit}
                    onChange={(value) => setParam("evidence.gateSkillCredit", value)}
                />
                <SwitchRow
                    label={t("steuer.p.gateObligation")}
                    hint={t("steuer.p.gateObligationHint")}
                    checked={p.evidence.gateObligationDischarge}
                    onChange={(value) => setParam("evidence.gateObligationDischarge", value)}
                />
                <SwitchRow
                    label={t("steuer.p.requireBehaviour")}
                    checked={p.evidence.requireBehaviour}
                    onChange={(value) => setParam("evidence.requireBehaviour", value)}
                />
                <SliderRow
                    label={t("steuer.p.reactionTarget")}
                    value={p.evidence.reactionTarget}
                    min={1}
                    max={5}
                    step={0.5}
                    format={(value) => value.toFixed(1)}
                    onChange={(value) => setParam("evidence.reactionTarget", value)}
                />
                <SliderRow
                    label={t("steuer.p.learningPass")}
                    value={Math.round(p.evidence.learningPassScaled * 100)}
                    min={0}
                    max={100}
                    step={1}
                    unit="%"
                    onChange={(value) => setParam("evidence.learningPassScaled", value / 100)}
                />
                <SliderRow
                    label={t("steuer.p.behaviourWindow")}
                    value={p.evidence.behaviourWindowDays.to}
                    min={7}
                    max={180}
                    unit={t("steuer.p.days")}
                    onChange={(value) => setParam("evidence.behaviourWindowDays.to", value)}
                />
            </Cluster>

            <Cluster icon={ShieldCheck} {...section("duties")}
                title={t("steuer.cluster.duties")}>
                <SwitchRow
                    label={t("steuer.p.neverDrop")}
                    hint={t("steuer.p.neverDropHint")}
                    checked={p.obligations.neverDrop}
                    onChange={(value) => setParam("obligations.neverDrop", value)}
                />
                <SliderRow
                    label={t("steuer.p.leadTime")}
                    hint={t("steuer.p.leadTimeHint")}
                    value={p.obligations.leadTimeMultiplier}
                    min={0}
                    max={6}
                    step={0.5}
                    format={(value) => `${value.toFixed(1)}×`}
                    onChange={(value) => setParam("obligations.leadTimeMultiplier", value)}
                />
            </Cluster>

            <Cluster icon={TrendingUp} {...section("gaps")}
                title={t("steuer.cluster.gaps")}>
                <SliderRow
                    label={t("steuer.p.minSize")}
                    value={p.gaps.minSize}
                    min={0}
                    max={4}
                    onChange={(value) => setParam("gaps.minSize", value)}
                />
                <SliderRow
                    label={t("steuer.p.maxLevelJump")}
                    value={p.gaps.maxLevelJump}
                    min={1}
                    max={4}
                    onChange={(value) => setParam("gaps.maxLevelJump", value)}
                />
                <SliderRow
                    label={t("steuer.p.tooBasic")}
                    value={p.ranking.tooBasicTolerance}
                    min={0}
                    max={4}
                    onChange={(value) => setParam("ranking.tooBasicTolerance", value)}
                />
            </Cluster>

            <Cluster icon={Database} {...section("sources")}
                title={t("steuer.cluster.sources")}>
                {ALL_SOURCES.map((source: SkillSource) => (
                    <SliderRow
                        key={source}
                        label={`${t(`source.${source}`)} — ${t("steuer.p.trust")}`}
                        value={Math.round(p.sources.trust[source] * 100)}
                        min={0}
                        max={100}
                        step={5}
                        unit="%"
                        onChange={(value) => setParam(`sources.trust.${source}`, value / 100)}
                    />
                ))}
                <SliderRow
                    label={t("steuer.p.minTrust")}
                    value={Math.round(p.sources.minTrust * 100)}
                    min={0}
                    max={100}
                    step={5}
                    unit="%"
                    onChange={(value) => setParam("sources.minTrust", value / 100)}
                />
                <ChoiceRow
                    label={t("steuer.p.sourceMode")}
                    value={p.sources.mode}
                    options={[
                        { value: "gate" as const, label: t("steuer.p.sourceMode.gate") },
                        { value: "discount" as const, label: t("steuer.p.sourceMode.discount") },
                    ]}
                    onChange={(value) => setParam("sources.mode", value)}
                />
            </Cluster>

            <Cluster icon={ListOrdered} {...section("sequencing")}
                title={t("steuer.cluster.sequencing")}>
                <SwitchRow
                    label={t("steuer.p.expand")}
                    checked={p.scheduling.prerequisites.expand}
                    onChange={(value) => setParam("scheduling.prerequisites.expand", value)}
                />
                <SliderRow
                    label={t("steuer.p.maxItems")}
                    value={Math.min(p.scheduling.maxItemsPerPeriod, 12)}
                    min={1}
                    max={12}
                    format={(value) => (value >= 12 ? "∞" : String(value))}
                    onChange={(value) =>
                        setParam("scheduling.maxItemsPerPeriod", value >= 12 ? 999 : value)
                    }
                />
                <SliderRow
                    label={t("steuer.p.maxCategories")}
                    hint={t("steuer.p.maxCategoriesHint")}
                    value={Math.min(p.scheduling.maxCategoriesPerPeriod, 6)}
                    min={1}
                    max={6}
                    format={(value) => (value >= 6 ? "∞" : String(value))}
                    onChange={(value) =>
                        setParam("scheduling.maxCategoriesPerPeriod", value >= 6 ? 9 : value)
                    }
                />
                <ChoiceRow
                    label={t("steuer.p.deferralMode")}
                    value={p.scheduling.deferralMode}
                    options={[
                        {
                            value: "chain-vs-remaining" as const,
                            label: t("steuer.p.deferralMode.chain"),
                        },
                        { value: "period-only" as const, label: t("steuer.p.deferralMode.period") },
                    ]}
                    onChange={(value) => setParam("scheduling.deferralMode", value)}
                />
                <SwitchRow
                    label={t("steuer.p.reinforcement")}
                    checked={p.reinforcement.enabled}
                    onChange={(value) => setParam("reinforcement.enabled", value)}
                />
                {p.reinforcement.enabled && (
                    <SliderRow
                        label={t("steuer.p.afterDays")}
                        value={p.reinforcement.afterDays}
                        min={7}
                        max={180}
                        unit={t("steuer.p.days")}
                        onChange={(value) => setParam("reinforcement.afterDays", value)}
                    />
                )}
            </Cluster>

            <Cluster
                icon={Layers}
                {...section("mix")}
                title={t("steuer.cluster.mix")}
                inactive={!p.mix.enabled}
                inactiveNote={t("steuer.mixInactive")}
            >
                <SwitchRow
                    label={t("steuer.p.mixEnabled")}
                    checked={p.mix.enabled}
                    onChange={(value) => setParam("mix.enabled", value)}
                />
                <WeightBar
                    entries={MODALITIES.map((modality) => ({
                        id: modality,
                        label: t(`steuer.mod.${modality}`),
                        value: p.mix.target[modality],
                    }))}
                />
                {MODALITIES.map((modality) => (
                    <SliderRow
                        key={modality}
                        label={t(`steuer.mod.${modality}`)}
                        value={p.mix.target[modality]}
                        min={0}
                        max={100}
                        step={5}
                        unit="%"
                        onChange={(value) => setParam(`mix.target.${modality}`, value)}
                    />
                ))}
            </Cluster>

            <Cluster
                icon={Banknote}
                {...section("cost")}
                title={t("steuer.cluster.cost")}
                inactive={!usdMode}
                inactiveNote={t("steuer.inactive")}
            >
                {(["praesenz", "webinar", "e-learning"] as const).map((format) => (
                    <SliderRow
                        key={format}
                        label={`${localizer.format(format)} — ${t("steuer.p.perHour")}`}
                        value={p.cost.perHourUsdByFormat[format]}
                        min={0}
                        max={400}
                        step={5}
                        onChange={(value) => setParam(`cost.perHourUsdByFormat.${format}`, value)}
                    />
                ))}
                <SliderRow
                    label={t("steuer.p.opportunity")}
                    hint={t("steuer.p.opportunityHint")}
                    value={p.cost.opportunityUsdPerHour}
                    min={0}
                    max={300}
                    step={5}
                    onChange={(value) => setParam("cost.opportunityUsdPerHour", value)}
                />
            </Cluster>

            <Cluster icon={GitBranch} {...section("governance")}
                title={t("steuer.cluster.governance")}>
                <p className="text-xs leading-relaxed text-muted-foreground">
                    {t("steuer.governance.body")}
                </p>
                </Cluster>
            </div>
        </div>
    )
}
